import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import {
  Avatar,
  Button,
  Input,
  Progress,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { PictureOutlined, PlusOutlined } from '@ant-design/icons';
// Api
import {
  GET_TICKET_EVENT_SALES_SUMMARIES,
  GET_TICKET_EVENTS,
} from 'api/ticketing/queries';
// Types
import {
  GetTicketEvents,
  GetTicketEventsVariables,
  GetTicketEvents_hostedTicketEvents_entities,
} from 'api/ticketing/types/GetTicketEvents';
import {
  GetTicketEventSalesSummaries,
  GetTicketEventSalesSummariesVariables,
  GetTicketEventSalesSummaries_ticketEventSalesSummaries,
} from 'api/ticketing/types/GetTicketEventSalesSummaries';
import {
  HostedTicketEventsOrderBy,
  TicketEventStatus,
} from 'api/graphql-global-types';
// Hooks
import { useServerTable } from 'pages/Events/hooks/useServerTable';
// Helpers
import {
  EVENT_STATUS_FILTER_OPTIONS,
  formatCount,
  formatEventDateTime,
  formatMoney,
  getEventHostName,
  getEventStatusMeta,
  isTaxApprovalPending,
  pluralize,
} from 'pages/Events/helpers';
// Constants
import {
  CREATE_TICKET_EVENT,
  EDIT_TICKET_EVENT_GEN,
  TICKET_EVENT_DETAILS_GEN,
} from 'constants/routes';
import { EventDetailsTab, EVENTS_PAGE_SIZE } from 'pages/Events/constants';
// UI
import PageToolbar from 'ui/PageToolbar/PageToolbar';
import RowActions from 'ui/RowActions/RowActions';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
import StatusTag from 'pages/Events/components/StatusTag/StatusTag';
import TicketingStatusAlert from 'pages/Events/components/TicketingStatusAlert/TicketingStatusAlert';
// Styles
import styles from './EventsTable.module.scss';

type TicketEventRow = GetTicketEvents_hostedTicketEvents_entities;

type SalesSummary = GetTicketEventSalesSummaries_ticketEventSalesSummaries;

type EventsTableProps = {
  ownerId?: string;
};

const SORT_COLUMNS: Record<string, HostedTicketEventsOrderBy> = {
  name: HostedTicketEventsOrderBy.Name,
  startsAt: HostedTicketEventsOrderBy.StartsAt,
  ticketPriceFrom: HostedTicketEventsOrderBy.TicketPrice,
};

const EventsTable = ({ ownerId }: EventsTableProps): JSX.Element => {
  const history = useHistory();
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<TicketEventStatus[]>([]);
  const table = useServerTable({
    sortColumns: SORT_COLUMNS,
    defaultSortBy: HostedTicketEventsOrderBy.CreatedAt,
    defaultPageSize: EVENTS_PAGE_SIZE,
  });
  const { sortOrder } = table;
  const [debouncedSearch] = useDebounce(search.trim(), 400);

  const { data, loading, error, refetch } = useQuery<
    GetTicketEvents,
    GetTicketEventsVariables
  >(GET_TICKET_EVENTS, {
    variables: {
      input: {
        ownerId: ownerId || null,
        searchTerm: debouncedSearch || null,
        statuses: statuses.length ? statuses : null,
        orderBy: table.sortBy,
        direction: table.direction,
        offset: table.offset,
        limit: table.pageSize,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const events = useMemo(() => data?.hostedTicketEvents.entities ?? [], [data]);
  const total = data?.hostedTicketEvents.total ?? 0;

  const eventIds = useMemo(() => events.map(({ id }) => id), [events]);

  const { data: salesData } = useQuery<
    GetTicketEventSalesSummaries,
    GetTicketEventSalesSummariesVariables
  >(GET_TICKET_EVENT_SALES_SUMMARIES, {
    variables: { input: { ticketEventIds: eventIds } },
    skip: !eventIds.length,
    fetchPolicy: 'cache-and-network',
  });

  const summaries = useMemo(
    () =>
      new Map<string, SalesSummary>(
        (salesData?.ticketEventSalesSummaries ?? []).map((summary) => [
          summary.ticketEventId,
          summary,
        ])
      ),
    [salesData]
  );

  const goToDetails = (row: TicketEventRow, tab?: EventDetailsTab): void => {
    history.push(
      `${TICKET_EVENT_DETAILS_GEN}/${row.id}${tab ? `/${tab}` : ''}`
    );
  };

  const handleCreate = (): void => {
    history.push(
      ownerId ? `${CREATE_TICKET_EVENT}?hostId=${ownerId}` : CREATE_TICKET_EVENT
    );
  };

  const columns = useMemo(
    () => [
      {
        title: 'Event',
        key: 'name',
        sorter: true,
        sortOrder: sortOrder('name'),
        render: (_: unknown, row: TicketEventRow) => (
          <div className={styles.eventCell}>
            <Avatar
              shape="square"
              size={48}
              src={row.coverImageUrl || undefined}
              icon={<PictureOutlined />}
              alt=""
            />
            <div className={styles.eventText}>
              <span className={styles.eventName}>{row.name}</span>
              <span className={styles.muted}>
                {row.venueName || 'No venue'}
                {row.stream ? ' · Linked stream' : ''}
              </span>
            </div>
          </div>
        ),
      },
      ...(ownerId
        ? []
        : [
            {
              title: 'Host',
              key: 'host',
              render: (_: unknown, row: TicketEventRow) =>
                getEventHostName(row),
            },
          ]),
      {
        title: 'Starts',
        key: 'startsAt',
        sorter: true,
        sortOrder: sortOrder('startsAt'),
        render: (_: unknown, row: TicketEventRow) =>
          formatEventDateTime(row.startsAt, row.timezone),
      },
      {
        title: 'Price from',
        key: 'ticketPriceFrom',
        sorter: true,
        sortOrder: sortOrder('ticketPriceFrom'),
        render: (_: unknown, row: TicketEventRow) =>
          row.ticketsEnabled ? formatMoney(row.ticketPriceFrom) : 'No tickets',
      },
      {
        title: 'Capacity',
        key: 'maxAdmissions',
        render: (_: unknown, row: TicketEventRow) =>
          row.maxAdmissions === null ? '—' : formatCount(row.maxAdmissions),
      },
      {
        title: 'Sold',
        key: 'sold',
        render: (_: unknown, row: TicketEventRow) => {
          const summary = summaries.get(row.id);

          if (!summary) {
            return '—';
          }

          return (
            <div className={styles.salesCell}>
              <span>
                {formatCount(summary.ticketsSold)}
                {summary.maxAdmissions
                  ? ` / ${formatCount(summary.maxAdmissions)}`
                  : ''}
              </span>
              {summary.maxAdmissions ? (
                <Progress
                  size="small"
                  showInfo={false}
                  percent={Math.min(
                    100,
                    Math.round(
                      (summary.ticketsSold / summary.maxAdmissions) * 100
                    )
                  )}
                />
              ) : null}
            </div>
          );
        },
      },
      {
        title: 'Revenue',
        key: 'revenue',
        render: (_: unknown, row: TicketEventRow) => {
          const summary = summaries.get(row.id);

          if (!summary) {
            return '—';
          }

          return (
            <div className={styles.salesCell}>
              <span>{formatMoney(summary.ticketRevenue)}</span>
              {summary.grossRevenue !== summary.ticketRevenue && (
                <span className={styles.muted}>
                  Gross {formatMoney(summary.grossRevenue)}
                </span>
              )}
              {summary.refundedOrders > 0 && (
                <span className={styles.muted}>
                  {formatCount(summary.refundedOrders)} refunded
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: 'Status',
        key: 'status',
        render: (_: unknown, row: TicketEventRow) => (
          <Space size={4} wrap>
            <StatusTag
              meta={getEventStatusMeta(row.status, row.ticketsEnabled)}
            />
            {isTaxApprovalPending(row) && <Tag>Tax not approved</Tag>}
          </Space>
        ),
      },
      {
        title: '',
        key: 'actions',
        align: 'right' as const,
        render: (_: unknown, row: TicketEventRow) => (
          // The row opens the details; the buttons must not open them too
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div onClick={(event) => event.stopPropagation()}>
            <RowActions
              onEdit={() => history.push(`${EDIT_TICKET_EVENT_GEN}/${row.id}`)}
              actions={[
                { label: 'Overview', onClick: () => goToDetails(row) },
                {
                  label: 'Orders & refunds',
                  onClick: () => goToDetails(row, EventDetailsTab.Orders),
                },
                {
                  label: 'Comps',
                  onClick: () => goToDetails(row, EventDetailsTab.Comps),
                },
                {
                  label: 'Participants & affiliates',
                  onClick: () => goToDetails(row, EventDetailsTab.People),
                },
                {
                  label: 'Matchcard',
                  onClick: () => goToDetails(row, EventDetailsTab.Matchcard),
                },
              ]}
            />
          </div>
        ),
      },
    ],
    // goToDetails only reads history
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ownerId, sortOrder, history, summaries]
  );

  return (
    <div className={styles.root}>
      <TicketingStatusAlert className={styles.alert} />

      <PageToolbar
        leading={
          <Space wrap>
            <Input.Search
              allowClear
              aria-label="Search events"
              placeholder="Search by event or venue"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                table.setPage(1);
              }}
              className={styles.search}
            />
            <Select<TicketEventStatus[]>
              mode="multiple"
              allowClear
              aria-label="Filter by status"
              placeholder="All statuses"
              value={statuses}
              options={EVENT_STATUS_FILTER_OPTIONS}
              onChange={(value) => {
                setStatuses(value);
                table.setPage(1);
              }}
              className={styles.statusFilter}
            />
          </Space>
        }
      >
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Create event
        </Button>
      </PageToolbar>

      {error ? (
        <LoadErrorAlert
          message="Failed to load events"
          error={error}
          onRetry={refetch}
        />
      ) : (
        <Table<TicketEventRow>
          rowKey="id"
          columns={columns}
          dataSource={events}
          loading={loading}
          onChange={table.onChange}
          onRow={(row) => ({
            onClick: () => goToDetails(row),
            className: styles.clickableRow,
          })}
          locale={{
            emptyText:
              debouncedSearch || statuses.length
                ? 'No events match these filters.'
                : 'No events yet. Create the first one.',
          }}
          pagination={{
            current: table.page,
            pageSize: table.pageSize,
            total,
            showSizeChanger: true,
            showTotal: (count) => pluralize(count, 'event'),
          }}
          scroll={{ x: 1200 }}
        />
      )}
    </div>
  );
};

export default EventsTable;
